/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { AppContext } from '../../../context';
import { Button } from '@goodtimeio/rain';
import { DateTime } from 'luxon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/pro-light-svg-icons';
import { getInterviewerLinkIntroApi, getLinkAvailabilityApi } from '../../../api';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import React, { useContext, useEffect, useState } from 'react';
import Wrapper from '../../../components/Wrapper';

import '../../Intro/styles.scss';
import { BlockDisplayEntity, InterviewBlock, InterviewerLinkIntroResponse } from '../types';
import { generateBlocksInTimeRange } from '../../../utils/time';
import { isEmail } from '../../../utils/commons';

const SuperdayEmailValidationPage: React.FC = () => {
    const { meetIntro, bootstrapSuperdayApp, translate, receiveSuperdayResults, setLinkAvailabilityData } =
        useContext(AppContext);
    const { customUrl, organizationSlug } = useParams<{ customUrl: string; organizationSlug: string }>();
    const { search } = useLocation();
    const [isInvalidEmailOnSuperday, setIsInvalidEmailOnSuperday] = useState(false);

    const primaryColor = meetIntro?.data?.meetCompanyColor ? meetIntro?.data?.meetCompanyColor : '#6400E0';
    const backgroundImage = meetIntro?.data?.meetBackgroundUrlList[0];
    const logo = meetIntro?.data?.organization?.logoUrl;

    let textColor = '#FFFFFF';
    if (meetIntro?.data?.meetFontColor) {
        textColor = meetIntro?.data?.meetFontColor;
    }

    useEffect(() => {
        const searchParams = new URLSearchParams(search);
        bootstrapSuperdayApp(customUrl, searchParams.get('source'));
    }, [customUrl, bootstrapSuperdayApp, search]);

    useEffect(() => {
        if (primaryColor) {
            document.documentElement.style.setProperty('--primary-date-picker-color', primaryColor);
        }
        if (textColor) {
            document.documentElement.style.setProperty('--primary-text-color', textColor);
        }
    }, [primaryColor, textColor]);

    useEffect(() => {
        handleInterviewerLinkIntro();
    }, []);

    const [interviewerLinkResponse, setInterviewerLinkResponse] = useState<InterviewerLinkIntroResponse>();
    const [email, setEmail] = useState('');
    const [isValidatingEmail, setIsValidatingEmail] = useState(false);
    const [isValidLink, setIsValidLink] = useState(true);
    const history = useHistory();
    const handleSubmitEmail = async () => {
        try {
            setIsValidatingEmail(true);
            const response = await getLinkAvailabilityApi({
                id: interviewerLinkResponse?.superdayInterviewerLink.id || '',
                email,
            });
            setIsValidatingEmail(false);
            if (response) {
                setLinkAvailabilityData({ ...response, completed: false });
                history.push(`/i/${organizationSlug}/pickDays/${customUrl}`);
            }
        } catch (error) {
            setIsValidatingEmail(false);
            setIsInvalidEmailOnSuperday(true);
        }
    };

    useEffect(() => {
        if (interviewerLinkResponse) {
            return;
        }
    }, [interviewerLinkResponse]);
    const handleInterviewerLinkIntro = async () => {
        try {
            setIsValidatingEmail(true);
            const response = await getInterviewerLinkIntroApi({
                organizationSlug,
                customUrl,
            });
            receiveSuperdayResults(generateOptions(response) ?? []);
            setInterviewerLinkResponse(response);
            setIsValidatingEmail(false);
        } catch {
            setIsValidatingEmail(false);
            setIsValidLink(false);
        }
    };

    const getDateDisplay = (isoString: string): string => {
        const dateTime = DateTime.fromISO(isoString);
        if (dateTime.isValid) {
            return dateTime.toFormat('MMMM dd, y');
        }
        return isoString;
    };

    const buildBlockDisplay = (blocks: InterviewBlock[]): BlockDisplayEntity[] => {
        if (blocks) {
            const blockDisplays: BlockDisplayEntity[] = [];
            const sortedBlocks = blocks.sort((blockA, blockB) => {
                const startTimeA = blockA.startTime;
                const startTimeB = blockB.startTime;

                if (startTimeA < startTimeB) {
                    return -1;
                } else if (startTimeA > startTimeB) {
                    return 1;
                } else {
                    return 0;
                }
            });
            sortedBlocks.forEach((block, indexOfBlock) => {
                const day = getDateDisplay(block.startTime);
                const existingDayIndex = blockDisplays.findIndex((blockToCheck) => blockToCheck.dateHeader === day);
                const blockToAddTo = blockDisplays[existingDayIndex];
                const newTimeInBlock = {
                    block: block,
                    blockIndex: indexOfBlock,
                };
                if (blockToAddTo) {
                    blockToAddTo.times.push(newTimeInBlock);
                    blockDisplays[existingDayIndex] = blockToAddTo;
                } else {
                    blockDisplays.push({
                        dateHeader: day,
                        times: [newTimeInBlock],
                    });
                }
            });
            return blockDisplays;
        }

        return [];
    };

    const generateOptions = (data?: InterviewerLinkIntroResponse) => {
        if (!data) {
            return;
        }
        const { blocks } = data;
        const mappedBlocks = buildBlockDisplay(blocks);
        const availability = mappedBlocks?.reduce((accum: any[], blockTime: BlockDisplayEntity) => {
            const result = blockTime.times.map((time, index) => {
                const { startTime, endTime, id } = time.block;
                const slots = generateBlocksInTimeRange(
                    DateTime.fromISO(startTime, { zone: 'UTC' }),
                    DateTime.fromISO(endTime, { zone: 'UTC' }),
                    data.eventDuration,
                );
                const date = DateTime.fromISO(startTime, { zone: 'UTC' }).toFormat('yyyy-MM-dd');
                const option = {
                    date,
                    options: slots.map((slot) => ({
                        ...slot,
                        durationTime: 0,
                        interviewBlockId: id || '',
                        interviewBlockStartTime: startTime,
                        interviewTimeBlockIndex: index,
                    })),
                };
                return option;
            });
            accum?.push(...result);
            return accum;
        }, []);
        return availability;
    };

    const handleEmailChange = (e: any) => {
        setEmail(e.currentTarget.value);
    };

    if (!isValidLink) {
        return (
            <Wrapper loading={meetIntro?.isLoading} backgroundImage={backgroundImage} logo={logo}>
                <div className="cardContainer">
                    <div className="cardContent">
                        <div className={'error'}>
                            This link is invalid or the corresponding SuperDay is already active. If you feel this is
                            incorrect, please contact the recruiting team.
                        </div>
                    </div>
                </div>
            </Wrapper>
        );
    }

    return (
        <Wrapper loading={meetIntro?.isLoading} backgroundImage={backgroundImage} logo={logo}>
            <div className="cardContainer">
                <div className="cardContent">
                    <div className="field">
                        <span>Please enter your email to provide your availability</span>
                        <br />
                        <span className="required">{translate('link:email')}</span>
                        <input
                            name="email"
                            className={'input'}
                            type="text"
                            onChange={handleEmailChange}
                            value={email}
                        />
                    </div>

                    {isInvalidEmailOnSuperday && (
                        <div className={'error'}>
                            Email submitted is not found on this interview. Please try entering your email again. If you
                            feel this is incorrect, please contact the recruiting team.
                        </div>
                    )}
                </div>
                <div className="cardFooter">
                    <Button
                        disabled={isValidatingEmail || !isEmail(email)}
                        className="continueButton"
                        onClick={handleSubmitEmail}
                    >
                        {isValidatingEmail ? <FontAwesomeIcon icon={faSpinner} spin /> : translate('signup:send')}
                    </Button>
                </div>
            </div>
        </Wrapper>
    );
};

export default SuperdayEmailValidationPage;
